<section aria-labelledby="adjust-search-title">
  <div class="card-header flex align-center gap-10">
    <app-icon
      name="tune"
      size="sm"
      color="custom"
      customColor="currentColor"
      role="presentation"
    ></app-icon>
    <h2 id="adjust-search-title" class="card-title fs-16 fw-700">Adjust Patient Record Search</h2>
    <button
      type="button"
      class="ghost-button fw-600"
      (click)="onCancel()"
      aria-label="Cancel adjust search"
    >
      <app-icon
        name="close"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span>Cancel</span>
    </button>
  </div>

  <div class="card-body">
    <div class="info-banner flex gap-10 align-start" role="note">
      <app-icon
        name="lightbulb"
        size="xs"
        color="custom"
        customColor="currentColor"
        role="presentation"
      ></app-icon>
      <span class="fs-12">{{ searchHint }}</span>
    </div>

    <div class="fields-grid">
      <div class="field">
        <label class="field__label fs-12 fw-600" for="adjust-first-name">First Name</label>
        <div
          class="field__input-with-clear"
          [class.field__input-with-clear--active]="firstNameValue()"
          [class.field__input-with-clear--invalid]="firstNameInvalid()"
        >
          <input
            id="adjust-first-name"
            type="text"
            class="field__input-bare"
            [value]="firstNameValue()"
            (input)="firstNameValue.set($any($event.target).value)"
            autocomplete="given-name"
            [attr.maxlength]="maxNameLength"
            [attr.aria-invalid]="firstNameInvalid()"
            [attr.aria-describedby]="firstNameInvalid() ? 'adjust-first-name-error' : null"
          />
          @if (firstNameValue()) {
          <button
            type="button"
            class="field__clear-btn"
            (click)="firstNameValue.set('')"
            aria-label="Clear First Name"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
        @if (firstNameInvalid()) {
        <p id="adjust-first-name-error" class="field__error fs-12" role="alert">
          Enter at least {{ minFieldLength }} characters for the first name.
        </p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="adjust-last-name">Last Name</label>
        <div
          class="field__input-with-clear"
          [class.field__input-with-clear--active]="lastNameValue()"
          [class.field__input-with-clear--invalid]="lastNameInvalid()"
        >
          <input
            id="adjust-last-name"
            type="text"
            class="field__input-bare"
            [value]="lastNameValue()"
            (input)="lastNameValue.set($any($event.target).value)"
            autocomplete="family-name"
            [attr.maxlength]="maxNameLength"
            [attr.aria-invalid]="lastNameInvalid()"
            [attr.aria-describedby]="lastNameInvalid() ? 'adjust-last-name-error' : null"
          />
          @if (lastNameValue()) {
          <button
            type="button"
            class="field__clear-btn"
            (click)="lastNameValue.set('')"
            aria-label="Clear Last Name"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
        @if (lastNameInvalid()) {
        <p id="adjust-last-name-error" class="field__error fs-12" role="alert">
          Enter at least {{ minFieldLength }} characters for the last name.
        </p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="adjust-dob">Date of Birth</label>
        <div
          class="field__input-with-clear"
          [class.field__input-with-clear--active]="dateOfBirthValue()"
          [class.field__input-with-clear--invalid]="dateOfBirthInvalid()"
        >
          <input
            id="adjust-dob"
            type="text"
            class="field__input-bare"
            [value]="dateOfBirthValue()"
            (input)="dateOfBirthValue.set($any($event.target).value)"
            placeholder="MM/DD/YYYY"
            inputmode="numeric"
            [attr.aria-invalid]="dateOfBirthInvalid()"
            [attr.aria-describedby]="dateOfBirthInvalid() ? 'adjust-dob-error' : null"
          />
          @if (dateOfBirthValue()) {
          <button
            type="button"
            class="field__clear-btn"
            (click)="dateOfBirthValue.set('')"
            aria-label="Clear Date of Birth"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
        @if (dateOfBirthInvalid()) {
        <p id="adjust-dob-error" class="field__error fs-12" role="alert">
          Enter the date of birth as MM/DD/YYYY.
        </p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="adjust-ssn">SSN (last 5)</label>
        <div
          class="field__input-with-clear"
          [class.field__input-with-clear--active]="ssnValue()"
          [class.field__input-with-clear--invalid]="ssnInvalid()"
        >
          <input
            id="adjust-ssn"
            type="text"
            class="field__input-bare"
            [value]="ssnValue()"
            (input)="ssnValue.set($any($event.target).value)"
            placeholder="Enter last 5 digits..."
            [attr.maxlength]="maxSsnLength"
            inputmode="numeric"
            autocomplete="off"
            [attr.aria-invalid]="ssnInvalid()"
            [attr.aria-describedby]="ssnInvalid() ? 'adjust-ssn-error' : null"
          />
          @if (ssnValue()) {
          <button
            type="button"
            class="field__clear-btn"
            (click)="clearSsn()"
            aria-label="Clear SSN"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
        @if (ssnInvalid()) {
        <p id="adjust-ssn-error" class="field__error fs-12" role="alert">
          Enter digits only for the last 5 of the SSN.
        </p>
        }
      </div>

      <div class="field">
        <label class="field__label fs-12 fw-600" for="adjust-mrn">MRN</label>
        <div
          class="field__input-with-clear"
          [class.field__input-with-clear--active]="mrnValue()"
          [class.field__input-with-clear--invalid]="mrnInvalid()"
        >
          <input
            id="adjust-mrn"
            type="text"
            class="field__input-bare"
            [value]="mrnValue()"
            (input)="mrnValue.set($any($event.target).value)"
            placeholder="Enter MRN..."
            [attr.maxlength]="maxMrnLength"
            [attr.aria-invalid]="mrnInvalid()"
            [attr.aria-describedby]="mrnInvalid() ? 'adjust-mrn-error' : null"
          />
          @if (mrnValue()) {
          <button
            type="button"
            class="field__clear-btn"
            (click)="mrnValue.set('')"
            aria-label="Clear MRN"
          >
            <app-icon
              name="close"
              size="xs"
              color="custom"
              customColor="currentColor"
              role="presentation"
            ></app-icon>
          </button>
          }
        </div>
        @if (mrnInvalid()) {
        <p id="adjust-mrn-error" class="field__error fs-12" role="alert">
          Enter at least {{ minFieldLength }} characters for the MRN.
        </p>
        }
      </div>
    </div>

    <div class="card-footer flex justify-between align-center">
      <button type="button" class="ghost-button fw-600" (click)="onResetToOriginal()">
        Reset to Original
      </button>
      <button
        type="button"
        class="primary-button fw-600"
        (click)="onRunSearch()"
        [disabled]="!canRunSearch()"
      >
        <app-icon
          name="person_search"
          size="xs"
          color="custom"
          customColor="currentColor"
          role="presentation"
        ></app-icon>
        <span>Run Search</span>
      </button>
    </div>
  </div>
</section>
